<script lang="ts">
  import { onMount } from "svelte";
  import {
    createLocalRunner,
    connect,
    lintKernelSource,
    customKernelParams,
    loadCustomKernel,
    effectiveVerifiable,
    type Runner,
    type RunRequest,
    type ParamSpec,
    type StatsMsg,
    type LintFinding,
  } from "../sdk";
  import { galleryList, kernelMeta } from "../kernels";
  import { encodePermalink, decodePermalink } from "./permalink";
  import { renderResults, renderResultEmptyState } from "./results";
  import { CUSTOM_KERNEL_TEMPLATE } from "./custom-template";
  import { forkSource } from "./kernel-sources";
  import { renderAbout } from "./about";
  import { renderContribute } from "./contribute";
  import { MESH_URL, meshEnabled } from "./mesh-config";
  import { createMeshPanel } from "./mesh-panel";
  import { installTooltips, infoDot } from "./tooltip";
  import { createRunVisual, type RunVisualHandle } from "./run-visual";
  import { createCodeEditor, type CodeEditorHandle } from "./code-editor";

  // App shell (Svelte). The app only ever touches compute through `Runner` (the seam): the
  // local pool and the mesh both satisfy it, and the toggle below selects which to use —
  // nothing else in the flow changes. Phase A of the utility redesign: this re-platforms the
  // former main.ts/index.html onto Svelte with 1:1 behavioural + visual parity, reusing the
  // existing pure-DOM modules (results / about / contribute / tooltip / mesh-panel) as-is.

  const CUSTOM_ID = "custom";

  // ── Reactive state (ported from the imperative module state in main.ts) ──
  let selectedKernelId = $state(galleryList[0]?.id ?? "");
  let nValue = $state(String(kernelMeta(galleryList[0]?.id ?? "")?.defaultN ?? 1_000_000));
  let seedValue = $state("experiment-42");
  let kernelSource = $state("");
  let meshOn = $state(false);
  let running = $state(false);
  let lintFindings = $state<LintFinding[]>([]);
  let customParamSpecs = $state<ParamSpec[]>([]);
  // Verifiable preview (plan §5): null = unknown/not-yet-evaluated.
  let verifiablePreview = $state<boolean | null>(null);
  // Which gallery kernel (or "__blank") the editor was last seeded from, so the toolbar can
  // highlight the active starting point. Null until the author forks something.
  let forkedFromId = $state<string | null>(null);
  // The contract help panel starts open; authors can collapse it once they know the rules.
  let contractCollapsed = $state(false);
  // Whether a run has produced a result yet. Before the first run the Result panel shows a
  // live example permalink (rec #1/#7) and the Live-run section stays hidden.
  let hasRun = $state(false);
  // True on the #about / #contribute overlay routes — suppresses the sticky run bar there.
  let overlayRoute = $state(false);

  // Param values per kernel id, kept across kernel switches.
  const paramValues: Record<string, Record<string, number>> = {};
  // Force re-render of the params block when values change (the store is a plain object).
  let paramsVersion = $state(0);

  const mesh = meshEnabled();
  const meshPanel = createMeshPanel();

  // Element refs.
  let resultsEl = $state<HTMLDivElement>();
  let meshPanelSlot = $state<HTMLDivElement>();
  let runVisualEl = $state<HTMLDivElement>();
  let runVisualVisible = $state(false);
  let editorEl = $state<HTMLDivElement>();
  let editor: CodeEditorHandle | null = null;

  // ── Derived ──
  const isCustom = $derived(selectedKernelId === CUSTOM_ID);
  const lintOk = $derived(!isCustom || lintFindings.length === 0);
  const activeSpecs = $derived<ParamSpec[]>(
    isCustom ? customParamSpecs : (kernelMeta(selectedKernelId)?.params ?? []) as ParamSpec[],
  );
  const showMeshCustomNotice = $derived(mesh && meshOn && isCustom);

  // Plain-language glosses (rec #2/#5). Shown on the shared info-dot tooltip layer.
  const KERNEL_GLOSS =
    "A simulation's code is called a kernel: a small, self-contained function that runs one random trial and combines the results. Pick a gallery kernel or write your own.";
  const TIER_GLOSS =
    "Verifiable: reproduces bit-for-bit across browser engines (Chrome / Firefox / Safari), because it uses only IEEE-specified arithmetic and the pinned fdlibm math library. Engine-specific: only guaranteed identical within the same browser engine.";

  function escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
  }

  // ── Kernel selection ──
  function selectKernel(id: string, opts: { reseedN?: boolean } = {}) {
    selectedKernelId = id;
    if (id === CUSTOM_ID) {
      if (!kernelSource.trim()) kernelSource = CUSTOM_KERNEL_TEMPLATE;
      // The editor mounts via $effect once #editor-host renders; sync its doc to kernelSource.
      editor?.setValue(kernelSource);
      runLint();
      refreshCustomParams();
    } else {
      if (opts.reseedN) {
        const meta = kernelMeta(id);
        if (meta) nValue = String(meta.defaultN);
      }
    }
  }

  // Fork-a-kernel (plan §5): load the gallery kernel's editable source into the editor and
  // switch to the custom route. Most users should never start from a blank textarea.
  function forkKernel(id: string, ev?: Event) {
    ev?.stopPropagation(); // don't also fire the card's select handler
    const src = id === "__blank" ? CUSTOM_KERNEL_TEMPLATE : forkSource(id);
    if (!src) return;
    kernelSource = src;
    selectedKernelId = CUSTOM_ID;
    forkedFromId = id;
    editor?.setValue(src);
    runLint();
    refreshCustomParams();
  }

  // Lazy-mount CodeMirror when the editor host appears (custom route). Keeps CM out of the
  // main bundle (plan §9) — it only loads when an author actually opens the editor.
  //
  // Leaving the custom route removes #cm-host from the DOM (bind:this resets editorEl to
  // undefined), which destroys CM's view but leaves our `editor` handle dangling. Tear it down
  // and null it here so a later fork re-mounts into the fresh host instead of seeing a stale
  // (truthy) handle and skipping the mount — the cause of the "editor doesn't show on the
  // second fork" bug.
  $effect(() => {
    if (isCustom && editorEl && !editor) {
      void createCodeEditor(editorEl, {
        doc: kernelSource,
        onChange: (value) => {
          kernelSource = value;
          onSourceInput();
        },
        lint: (src) => lintKernelSource(src),
      }).then((handle) => {
        editor = handle;
      });
    } else if ((!isCustom || !editorEl) && editor) {
      editor.destroy();
      editor = null;
    }
  });

  // ── Params ──
  function paramStore(): Record<string, number> {
    return (paramValues[selectedKernelId] ??= {});
  }

  function paramValue(spec: ParamSpec): number {
    const store = paramStore();
    if (store[spec.key] === undefined) store[spec.key] = spec.default;
    return store[spec.key]!;
  }

  function commitParam(key: string, raw: string) {
    const v = Number(raw);
    if (Number.isFinite(v)) {
      paramStore()[key] = v;
      paramsVersion++;
    }
  }

  function resetParams() {
    delete paramValues[selectedKernelId];
    paramsVersion++;
  }

  let customParamsTimer: ReturnType<typeof setTimeout> | undefined;
  function refreshCustomParams() {
    if (!isCustom) return;
    clearTimeout(customParamsTimer);
    customParamsTimer = setTimeout(async () => {
      const src = kernelSource;
      const clean = lintKernelSource(src).length === 0;
      const specs = clean ? await customKernelParams(src) : [];
      if (isCustom) customParamSpecs = specs as ParamSpec[];
      // Verifiable preview (plan §5): load the kernel (lint-clean only) to read its declared
      // `verifiable` flag, then apply effectiveVerifiable — so authors learn the cross-engine
      // rules before running. Unknown while it fails to load / lint.
      if (!clean) {
        if (isCustom) verifiablePreview = null;
        return;
      }
      try {
        const k = await loadCustomKernel(src);
        const declared = !!k.verifiable;
        if (isCustom) verifiablePreview = effectiveVerifiable(declared, src);
      } catch {
        if (isCustom) verifiablePreview = null;
      }
    }, 350);
  }

  // ── Lint (live) ──
  function runLint(): boolean {
    if (!isCustom) {
      lintFindings = [];
      return true;
    }
    lintFindings = lintKernelSource(kernelSource);
    return lintFindings.length === 0;
  }

  function onSourceInput() {
    runLint();
    refreshCustomParams();
  }

  // ── Run ──
  function currentRequest(): RunRequest {
    const params = { ...(paramValues[selectedKernelId] ?? {}) };
    if (isCustom) {
      return { kernelId: CUSTOM_ID, seed: seedValue, n: Number(nValue), source: kernelSource, params };
    }
    return { kernelId: selectedKernelId, seed: seedValue, n: Number(nValue), params };
  }

  // The local pool is always available and reused across runs; the mesh runner is per-run.
  const localRunner: Runner = createLocalRunner();

  // The live materialise grid + convergence chart (plan §4). Created per run, fed `onChunk`,
  // and disposed when the canonical result panel renders. It is a pure VIEW over advisory
  // events — it never affects the result, which the Runner reduces canonically.
  let runVisual: RunVisualHandle | null = null;

  async function run(req: RunRequest) {
    if (req.kernelId === CUSTOM_ID && !runLint()) return; // never dispatch a kernel that fails lint
    const out = resultsEl;
    const viz = runVisualEl;
    if (!out || !viz) return; // mounted before any run can be triggered
    running = true;
    const wantMesh = mesh && meshOn && MESH_URL;

    // Build the live visual front-and-centre; the result panel shows a slim progress line.
    runVisual?.dispose();
    const resultUnit = (isCustom ? "result" : kernelMeta(req.kernelId)?.resultUnit) ?? "result";
    runVisual = createRunVisual(viz, { resultUnit, mode: wantMesh ? "mesh" : "local" });
    runVisualVisible = true;

    const setProgressLine = (txt: string) => {
      out.innerHTML = `<div class="progress-line"><span class="spinner"></span>${txt}</div>`;
    };
    const hooks = {
      onProgress: (p: { completed: number; total: number }) =>
        setProgressLine(`${p.completed.toLocaleString()} / ${p.total.toLocaleString()} iterations…${wantMesh ? " (mesh)" : ""}`),
      onChunk: (c: import("../sdk").ChunkEvent) => runVisual?.onChunk(c),
    };
    setProgressLine(`Running ${req.n.toLocaleString()} iterations…${wantMesh ? " on the mesh" : ""}`);

    try {
      let result;
      if (wantMesh) {
        meshPanel.setConnState("connecting");
        const meshRunner = connect({
          url: MESH_URL!,
          onStats: (s: StatsMsg) => {
            meshPanel.update(s);
            meshPanel.setConnState("running");
          },
        });
        try {
          result = await meshRunner.run(req, hooks);
          meshPanel.setConnState("done");
        } catch (meshErr) {
          meshPanel.setConnState("error", (meshErr as Error).message);
          setProgressLine("Mesh unavailable — running locally…");
          result = await localRunner.run(req, hooks);
        } finally {
          meshRunner.dispose();
        }
      } else {
        result = await localRunner.run(req, hooks);
      }
      const permalink = await encodePermalink(req);
      renderResults(out, result, permalink);
      hasRun = true; // the Result panel now holds a real result, not the example
      history.replaceState(null, "", permalink); // shareable, reproducible URL
    } catch (err) {
      out.innerHTML = `<span class="muted">Error: ${escapeHtml((err as Error).message)}</span>`;
      runVisualVisible = false;
    } finally {
      running = false;
    }
  }

  // ── Routing + boot ──
  function showOverlayRoute(): boolean {
    const isWorkerTab = new URLSearchParams(location.search).get("worker") === "1";
    if (isWorkerTab && location.hash !== "#contribute") {
      location.hash = "#contribute";
      return true;
    }
    if (location.hash === "#contribute") {
      overlayRoute = true;
      renderContribute();
      return true;
    }
    if (location.hash === "#about") {
      overlayRoute = true;
      renderAbout();
      return true;
    }
    overlayRoute = false;
    return false;
  }

  async function boot() {
    if (showOverlayRoute()) return;

    const decoded = await decodePermalink(location.hash);
    if (decoded) {
      const { req } = decoded;
      const restoreParams = (id: string) => {
        if (req.params && Object.keys(req.params).length) paramValues[id] = { ...req.params };
        paramsVersion++;
      };
      if (req.kernelId === CUSTOM_ID && req.source) {
        kernelSource = req.source;
        restoreParams(CUSTOM_ID);
        selectKernel(CUSTOM_ID);
        nValue = String(req.n);
        seedValue = req.seed;
        run(req);
        return;
      }
      if (kernelMeta(req.kernelId)) {
        restoreParams(req.kernelId);
        selectKernel(req.kernelId);
        nValue = String(req.n);
        seedValue = req.seed;
        run(req);
        return;
      }
    }
    // No (valid) permalink — seed N + params from the default kernel.
    selectKernel(selectedKernelId, { reseedN: true });
  }

  // The mesh panel host (#meshPanelSlot) only exists while the "Run across volunteer browsers"
  // toggle is on (rec: hide mesh box until checked), so append the panel element whenever the
  // slot appears rather than once at mount.
  $effect(() => {
    if (meshPanelSlot && meshPanel.el.parentElement !== meshPanelSlot) {
      meshPanelSlot.appendChild(meshPanel.el);
    }
  });

  // Result panel empty state (rec #1/#7): show a live example permalink until the first real
  // run. Re-injected whenever the panel is empty (e.g. after route changes) and not yet run.
  $effect(() => {
    if (resultsEl && !hasRun && resultsEl.childElementCount === 0) {
      renderResultEmptyState(resultsEl);
    }
  });

  onMount(() => {
    installTooltips(); // one global delegated listener for every .info-dot
    boot();
    const onHash = () => {
      if (location.hash === "#about") { overlayRoute = true; renderAbout(); }
      else if (location.hash === "#contribute") { overlayRoute = true; renderContribute(); }
      else { overlayRoute = false; }
    };
    window.addEventListener("hashchange", onHash);
    // Enter-to-run (rec #4): the page-level shortcut runs the current request, but never while
    // the user is typing in the editor, a text field, or a textarea.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === "TEXTAREA" || t?.isContentEditable || t?.closest(".cm-editor")) return;
      if (tag === "INPUT" && (t as HTMLInputElement).type === "text") return;
      if (overlayRoute || running || !lintOk) return;
      run(currentRequest());
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("keydown", onKey);
    };
  });
</script>

<main class="container" id="playground">
  <h1>Monte Carlo, reproducible to the bit.</h1>
  <p class="lede">
    Pick a simulation, run millions of iterations across your own browser's Web Workers, and
    get a result with a permalink. Anyone who opens the link re-derives the same result - bit
    for bit. No backend, no sign-in.
  </p>

  <details class="how-it-works">
    <summary>How it works</summary>
    <div class="how-body">
      <ol>
        <li><strong>Pick a simulation</strong> (or paste your own) — a small piece of code, called a <em>kernel</em>, that runs one random trial at a time.</li>
        <li><strong>Run it</strong> — millions of trials are split into chunks and run in parallel across your browser's Web Workers, right on this page. Nothing is sent to a server.</li>
        <li><strong>Share the permalink</strong> — the result and its inputs are encoded in the URL. Open that link on any machine and you re-derive the <em>exact same bits</em>, because the result depends only on the simulation, its parameters, N and the seed.</li>
      </ol>
    </div>
  </details>

  <section class="section">
    <div class="section-head">1 · Pick a simulation {@html infoDot(escapeHtml(KERNEL_GLOSS))}</div>
    <div class="gallery">
      {#each galleryList as k (k.id)}
        <div class="kernel-card-wrap">
          <button
            type="button"
            class="kernel-card"
            class:active={selectedKernelId === k.id}
            aria-pressed={selectedKernelId === k.id}
            onclick={() => selectKernel(k.id, { reseedN: true })}
          >
            <span class="k-card-head">
              <span class="k-name">{k.label}</span>
              <span class="tier-tag {k.verifiable ? 'verified' : 'unverified'}">
                <span class="dot"></span>{k.verifiable ? "verifiable" : "engine-specific"}
              </span>
            </span>
            <span class="k-blurb">{k.blurb}</span>
          </button>
          <button
            type="button"
            class="fork-link"
            title={`Load ${k.label}'s source into the editor`}
            onclick={() => forkKernel(k.id)}
          >Fork this kernel →</button>
        </div>
      {/each}
      <div class="kernel-card-wrap">
        <button
          type="button"
          class="kernel-card"
          class:active={selectedKernelId === CUSTOM_ID}
          aria-pressed={selectedKernelId === CUSTOM_ID}
          onclick={() => selectKernel(CUSTOM_ID)}
        >
          <span class="k-card-head">
            <span class="k-name">Custom kernel ✎</span>
          </span>
          <span class="k-blurb"
            >Paste your own deterministic kernel. Loaded as a module under a strict CSP and
            checked by the determinism linter.</span
          >
        </button>
      </div>
    </div>
    <p class="tier-legend muted">
      <span class="tier-tag verified"><span class="dot"></span>verifiable</span> = reproduces bit-for-bit across browser engines.
      <span class="tier-tag unverified"><span class="dot"></span>engine-specific</span> = identical only within the same engine.
      {@html infoDot(escapeHtml(TIER_GLOSS))}
    </p>
  </section>

  {#if isCustom}
    <section class="section subsection" id="editor-section">
      <div class="subsection-head">Write your kernel</div>
      <div class="card">
        <div class="editor-layout" class:contract-collapsed={contractCollapsed}>
          <div class="editor-main">
            <div class="editor-toolbar">
              <span class="editor-fork-hint muted">Start from a gallery kernel:</span>
              {#each galleryList as k (k.id)}
                <button
                  type="button"
                  class="template-chip"
                  class:active={forkedFromId === k.id}
                  aria-pressed={forkedFromId === k.id}
                  onclick={() => forkKernel(k.id)}
                >{k.label}</button>
              {/each}
              <button
                type="button"
                class="template-chip"
                class:active={forkedFromId === "__blank"}
                aria-pressed={forkedFromId === "__blank"}
                onclick={() => forkKernel("__blank")}
              >Blank template</button>
            </div>

            <!-- CodeMirror mounts here (lazy). Inline lint markers show on the offending line. -->
            <div class="cm-host" bind:this={editorEl}>
              {#if contractCollapsed}
                <!-- When the contract aside is collapsed the editor spans edge-to-edge; this
                     floating control keeps the re-open affordance reachable. -->
                <button
                  type="button"
                  class="contract-show"
                  aria-expanded="false"
                  onclick={() => (contractCollapsed = false)}
                >Contract ▸</button>
              {/if}
            </div>

            <!-- Verifiable preview (plan §5): learn the cross-engine rules before running. -->
            <div class="verifiable-preview">
              {#if verifiablePreview === true}
                <span class="status-pill verified"><span class="dot"></span>Will be verifiable</span>
                <span class="muted">Reproduces bit-for-bit across browser engines (arithmetic / sqrt / the fdlibm math shim only).</span>
              {:else if verifiablePreview === false}
                <span class="status-pill unverified"><span class="dot"></span>Engine-specific</span>
                <span class="muted">Runs deterministically, but isn't marked cross-engine verifiable (set <span class="mono">verifiable: true</span> and use only the <span class="mono">math</span> shim for transcendentals).</span>
              {:else}
                <span class="muted">Fix the issues below to preview whether this kernel is cross-engine verifiable.</span>
              {/if}
            </div>

            {#if lintFindings.length === 0}
              <div class="lint-panel ok"><span class="dot"></span>No determinism issues — ready to run.</div>
            {:else}
              <div class="lint-panel err">
                <div class="lint-title">
                  {lintFindings.length} issue{lintFindings.length > 1 ? "s" : ""} — fix before running:
                </div>
                {#each lintFindings as f}
                  <div class="lint-item">
                    {#if f.line}<span class="lint-line">line {f.line}</span>{/if}{f.message}
                  </div>
                {/each}
              </div>
            {/if}
          </div>

          <!-- Inline contract help (plan §5): the chunk/reduce contract + the determinism rules
               beside the editor, not buried in prose. Collapsing it lets the editor span
               edge-to-edge; the floating "Contract ▸" button over the editor re-opens it. -->
          {#if !contractCollapsed}
          <aside class="editor-help-panel">
            <button
              type="button"
              class="contract-toggle"
              aria-expanded="true"
              onclick={() => (contractCollapsed = true)}
            >
              <h3>The kernel contract</h3>
              <span class="contract-chevron" aria-hidden="true">▾</span>
            </button>
            <p>A self-contained ES module that <span class="mono">export default</span> an object:</p>
            <pre class="contract-sig">chunk(count, rng, math, params) → partial
reduce(orderedPartials) → {'{'} result, samples {'}'}</pre>
            <ul class="contract-rules">
              <li><strong>Randomness only from <span class="mono">rng</span></strong> — never <span class="mono">Math.random</span>, <span class="mono">Date</span>, <span class="mono">performance</span>, or <span class="mono">crypto</span>.</li>
              <li><strong>Transcendentals only from <span class="mono">math</span></strong> (<span class="mono">math.log/exp/sin/cos/pow</span>) — never global <span class="mono">Math.*</span>. This keeps you on the cross-engine fdlibm path.</li>
              <li><strong>Reduce in the given order</strong> — partials arrive in fixed chunk-index order; combine them as-is. Don't sort or rely on arrival order.</li>
            </ul>
            <p class="contract-why muted">Why: a result must depend only on <span class="mono">(kernel, seed, N, params)</span>. These rules are what make your permalink reproduce bit-for-bit on anyone's machine. The linter enforces them — it loads your kernel as a module under a strict CSP, never <span class="mono">eval</span>.</p>
          </aside>
          {/if}
        </div>
      </div>
    </section>
  {/if}

  <section class="section">
    <div class="section-head">2 · Configure</div>
    <div class="card">
      {#key paramsVersion}
        {#if activeSpecs.length > 0}
          <div class="config-group">
            <div class="config-group-label">Simulation parameters</div>
            <div class="kernel-params">
              <div class="param-grid">
                {#each activeSpecs as spec (spec.key)}
                  {@const bounded = spec.min !== undefined && spec.max !== undefined}
                  <div class="param-field">
                    <label class="param-label" for={`p-${spec.key}`}>
                      {spec.label}{#if spec.unit}<span class="param-unit"> ({spec.unit})</span>{/if}
                      {#if spec.hint}{@html infoDot(escapeHtml(spec.hint))}{/if}
                    </label>
                    <div class="param-inputs">
                      <input
                        id={`p-${spec.key}`}
                        class="param-number"
                        type="number"
                        value={paramValue(spec)}
                        min={spec.min}
                        max={spec.max}
                        step={spec.step}
                        oninput={(e) => commitParam(spec.key, e.currentTarget.value)}
                      />
                      {#if bounded}
                        <input
                          class="param-slider"
                          type="range"
                          value={paramValue(spec)}
                          min={spec.min}
                          max={spec.max}
                          step={spec.step}
                          aria-label={`${spec.label} slider`}
                          oninput={(e) => commitParam(spec.key, e.currentTarget.value)}
                        />
                      {/if}
                    </div>
                  </div>
                {/each}
              </div>
              <button type="button" class="param-reset" onclick={resetParams}>Reset to defaults</button>
            </div>
          </div>
        {/if}
      {/key}

      <div class="config-group">
        <div class="config-group-label">Run settings</div>
        <div class="controls">
          <div class="field">
            <label for="n">Iterations (N)</label>
            <input id="n" type="number" min="100" step="100" bind:value={nValue} />
          </div>
          <div class="field">
            <label for="seed">Seed</label>
            <input id="seed" type="text" bind:value={seedValue} />
          </div>
        </div>
      </div>

      {#if mesh}
        <div class="config-group">
          <div class="config-group-label">Where to run</div>
          <div class="mesh-controls">
            <label class="mesh-toggle">
              <input type="checkbox" bind:checked={meshOn} />
              <span>Run across volunteer browsers</span>
            </label>
            <span class="mesh-controls-hint muted">
              The <em>mesh</em> — distribute this job across other people's connected browsers
              instead of running it locally.
              <a href="#contribute" target="_blank" rel="noopener">Open a worker tab →</a>
            </span>
            {#if meshOn}
              {#if showMeshCustomNotice}
                <div class="mesh-custom-notice">
                  ⚠︎ This custom kernel will run on other people's machines. It's checked by the
                  determinism linter and runs sandboxed with no network access, but that's
                  best-effort — only distribute code you'd be comfortable having others run.
                </div>
              {/if}
              <div bind:this={meshPanelSlot}></div>
            {/if}
          </div>
        </div>
      {/if}

      <p class="config-note muted">
        The result depends only on the simulation, its parameters, N and the seed — never on
        where it runs or how many machines it uses.
      </p>
    </div>
  </section>

  <section class="section">
    <div class="section-head">3 · Run</div>
    <div class="card run-card">
      <button class="btn btn-run" disabled={running || !lintOk} onclick={() => run(currentRequest())}>
        {running ? "Running…" : "Run"}
      </button>
      <span class="run-hint muted">Press <kbd>⏎</kbd> to run.</span>
    </div>
  </section>

  <section class="section" class:rv-hidden={!runVisualVisible}>
    <div class="section-head">Live run</div>
    <div class="card rv-card" bind:this={runVisualEl}></div>
  </section>

  <section class="section">
    <div class="section-head">4 · Result</div>
    <div class="card" bind:this={resultsEl} aria-live="polite" aria-atomic="true">
      <!-- Empty-state / example is injected by renderResultEmptyState() on mount; a real run
           replaces it via renderResults(). -->
    </div>
  </section>

</main>

<!-- Sticky run bar (rec #4): keeps the primary action reachable while the user scrolls long
     parameter lists. Same handler as the step-3 Run button; hidden on overlay routes. -->
{#if !overlayRoute}
  <div class="sticky-run" class:running>
    <div class="sticky-run-inner">
      <span class="sticky-run-label">
        {#if isCustom}Custom kernel{:else}{kernelMeta(selectedKernelId)?.label ?? "Simulation"}{/if}
        · N={Number(nValue).toLocaleString()}
      </span>
      <button class="btn btn-run" disabled={running || !lintOk} onclick={() => run(currentRequest())}>
        {running ? "Running…" : "Run"}
      </button>
    </div>
  </div>
{/if}
